import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Edit, Plus, Grid3X3 } from "lucide-react";
import { CreateCheckListForm, Section } from "../../types/api";
import { useFireSafetyAudits } from "../../store/store";
import { useLocation } from "react-router-dom";
const CreateCheckList: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<CreateCheckListForm | null>(null);
  const location = useLocation();
  const { mode, id } = location.state || {};

  const { currentChecklist, createCheckList, updateCheckList, getCheckList } =
    useFireSafetyAudits();
  const clearForm = () => {
    setFormData(null);
    setSections([]);
    setCurrentStep(1);
    useFireSafetyAudits.setState({ currentChecklist: null });
  };

  const [sections, setSections] = useState<Section[]>([]);
  const navigate = useNavigate();
  const [moduleName, setModuleName] = useState<string>("");

  const getModuleName = (): string | undefined => {
    const path = location.pathname;
    if (path.includes("/hazardous-materials")) {
      return "hazardous_materials";
    } else if (
      path.includes("/equipment-management/maintenance-scheduling-and-tracking")
    ) {
      return "equipment_management";
    } else if (
      path.includes("/fire-system") ||
      path.startsWith("/fire-safety-audits/create-check-list")
    ) {
      return "fire_system";
    }
    return undefined;
  };
  const myModuleName = getModuleName() || "";
  useEffect(() => {
    if (myModuleName) {
      setModuleName(myModuleName);
      if (id && mode === "edit") {
        getCheckList(id, myModuleName);
      }
    }
  }, [myModuleName, id, mode, getCheckList]);
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;

    setFormData((prev) => {
      const baseData = prev || {
        checkList: "",
        itemNo: undefined,
        riskRating: undefined,
        appliesToArea: "",
        auditType: "",
        moduleName: moduleName || "",
        checkListDescription: "",
        isActive: true,
      };
      return {
        ...baseData,
        [name]: type === "number" ? parseInt(value) || undefined : value,
        moduleName: moduleName,
      };
    });
  };
  const handleToggleActive = () => {
    setFormData((prev) => {
      const baseData = prev || {
        checkList: "",
        itemNo: undefined,
        riskRating: undefined,
        appliesToArea: "",
        auditType: "",
        moduleName: moduleName || "",
        checkListDescription: "",
        isActive: true,
      };
      return {
        ...baseData,
        isActive: !baseData.isActive,
      };
    });
  };
  const handleCancel = () => {
    clearForm();
    if (moduleName === "hazardous_materials") {
      navigate("/hazardous-materials/audits/check-list");
    } else if (moduleName === "equipment_management") {
      navigate(
        "/equipment-management/maintenance-scheduling-and-tracking/check-list",
      );
    } else {
      navigate("/fire-safety-audits/check-list");
    }
  };
  const handleNext = () => {
    if (!formData) {
      alert(
        "Please fill in all required fields: Checklist Name, Item No, Risk Rating, and Audit Type",
      );
      return;
    }
    // Validate required fields before proceeding
    if (
      !formData.checkList ||
      formData.itemNo === undefined ||
      !formData.riskRating ||
      !formData.auditType
    ) {
      alert(
        "Please fill in all required fields: Checklist Name, Item No, Risk Rating, and Audit Type",
      );
      return;
    }

    if (currentStep === 1) {
      setCurrentStep(2);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };
  useEffect(() => {
    if (currentChecklist) {
      setFormData({
        checkList: currentChecklist.checkList || "",
        itemNo: currentChecklist.itemNo,
        riskRating: currentChecklist.riskRating,
        appliesToArea: currentChecklist.appliesToArea || "",
        auditType: currentChecklist.auditType || "",
        moduleName: moduleName || "",
        checkListDescription:
          (currentChecklist as any).checkListDescription || "",
        isActive:
          typeof currentChecklist.isActive === "boolean"
            ? currentChecklist.isActive
            : true,
      });
      setSections((currentChecklist as any).sections || []);
    }
  }, [currentChecklist]);
  const handelUpdate = async () => {
    if (!formData) {
      alert("Form data is required");
      return;
    }
    if (!moduleName) {
      alert("Module name is required");
      return;
    }
    const completeData = {
      ...formData,
      sections: sections,
      moduleName: moduleName,
    };
    try {
      await updateCheckList(id, completeData, moduleName);
      clearForm();
      if (moduleName === "hazardous_materials") {
        navigate("/hazardous-materials/audits/check-list");
      } else if (moduleName === "equipment_management") {
        navigate(
          "/equipment-management/maintenance-scheduling-and-tracking/check-list",
        );
      } else {
        navigate("/fire-safety-audits/check-list");
      }
    } catch (error) {
      console.error("Error updating checklist:", error);
    }
  };
  const handleSave = async () => {
    if (!formData) {
      alert("Form data is required");
      return;
    }
    if (!moduleName) {
      alert("Module name is required");
      return;
    }
    const completeData = {
      ...formData,
      sections: sections,
      moduleName: moduleName,
    };
    try {
      await createCheckList(completeData, moduleName);
      clearForm();
      if (moduleName === "hazardous_materials") {
        navigate("/hazardous-materials/audits/check-list");
      } else if (moduleName === "equipment_management") {
        navigate(
          "/equipment-management/maintenance-scheduling-and-tracking/check-list",
        );
      } else {
        navigate("/fire-safety-audits/check-list");
      }
    } catch (error) {
      console.error("Error creating checklist:", error);
    }
  };
  const addSection = () => {
    const newSection: Section = {
      id: Date.now().toString(),
      title: "Enter Header",
      moduleName: moduleName || "",
      questions: [
        {
          id: `${Date.now()}-1`,
          text: "",
          moduleName: moduleName || "",
          isEditing: false,
        },
      ],
    };
    setSections((prev) => [...prev, newSection]);
  };

  const deleteSection = (sectionId: string) => {
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const updateSectionTitle = (sectionId: string, newTitle: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, title: newTitle } : section,
      ),
    );
  };

  const addQuestion = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: [
                ...section.questions,
                {
                  id: `${sectionId}-${Date.now()}`,
                  text: "",
                  moduleName: moduleName || "",
                  isEditing: false,
                },
              ],
            }
          : section,
      ),
    );
  };
  const updateQuestion = (
    sectionId: string,
    questionId: string,
    newText: string,
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map((q) =>
                q.id === questionId ? { ...q, text: newText } : q,
              ),
            }
          : section,
      ),
    );
  };

  const deleteQuestion = (sectionId: string, questionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.filter((q) => q.id !== questionId),
            }
          : section,
      ),
    );
  };

  const toggleQuestionEdit = (sectionId: string, questionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map((q) =>
                q.id === questionId ? { ...q, isEditing: !q.isEditing } : q,
              ),
            }
          : section,
      ),
    );
  };

  const handlePlusClick = (sectionId: string, questionId: string) => {
    // Save the current question (even if empty)
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              questions: section.questions.map((q) =>
                q.id === questionId ? { ...q, text: q.text || "" } : q,
              ),
            }
          : section,
      ),
    );

    // Add a new empty question
    addQuestion(sectionId);
  };

  if (currentStep === 1) {
    return (
      <div className="px-2 py-4 w-full h-full bg-gray-50">
        {/* Header */}
        <div className="mb-8 mt-14">
          <h1 className="text-2xl font-semibold text-blue-900">
            {mode === "edit"
              ? "Update Audit Check List"
              : "Create Audit Check List"}
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* Row 1 - 4 columns */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            {/* Checklist Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Checklist Name
              </label>
              <input
                type="text"
                name="checkList"
                value={formData?.checkList || ""}
                onChange={handleInputChange}
                placeholder="Name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Item No */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Item No
              </label>
              <input
                type="number"
                name="itemNo"
                value={formData?.itemNo || ""}
                onChange={handleInputChange}
                placeholder="Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Risk Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk Rating
              </label>
              <div className="relative">
                <select
                  name="riskRating"
                  value={formData?.riskRating || ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Select Risk Rating</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Applies To Area */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applies To Area
              </label>
              <input
                type="text"
                name="appliesToArea"
                value={formData?.appliesToArea || ""}
                onChange={handleInputChange}
                placeholder="Area"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Row 2 - 3 columns */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Audit Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audit Type
              </label>
              <div className="relative">
                <select
                  name="auditType"
                  value={formData?.auditType || ""}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Select Audit Type</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Semi-Annual">Semi-Annual</option>
                  <option value="Annual">Annual</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>

            {/* Checklist Item Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Checklist Item Description
              </label>
              <textarea
                name="checkListDescription"
                value={formData?.checkListDescription || ""}
                onChange={handleInputChange}
                placeholder="Description"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Is Active Toggle */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Status
              </label>
              <button
                type="button"
                onClick={handleToggleActive}
                style={{
                  borderColor: "#edf2f9",
                  outline: "none",
                  boxShadow: "none",
                }}
                className="relative flex h-8 w-24 items-center rounded-full border bg-[#edf2f9] px-1 transition-all duration-300 focus:outline-none focus:ring-0 active:outline-none active:ring-0"
              >
                {/* Toggle Thumb */}
                <span
                  className={`absolute top-[3px] h-6 w-6 rounded-full shadow-sm transition-all duration-300 ${
                    formData?.isActive
                      ? "left-[70px] bg-[#0fa9a6]"
                      : "left-[2px] bg-red-500"
                  }`}
                />
                {/* Toggle Text */}
                <span
                  className={`w-full text-[11px] font-medium ${
                    formData?.isActive
                      ? "text-[#0fa9a6] text-left pl-2 pr-8"
                      : "text-red-500 text-right pr-2 pl-8"
                  }`}
                >
                  {formData?.isActive ? "Active" : "Inactive"}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-2 bg-[#61748e] border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
          >
            CANCEL
          </button>
          <button
            onClick={handleNext}
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          >
            NEXT
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col px-2 py-4">
      {/* Main Header - Fixed height */}
      <div className="flex-shrink-0 p-8 mt-14">
        <div className="flex justify-between items-start mb-6">
          <h1 className="text-2xl font-semibold text-blue-900">
            {mode === "edit"
              ? "Update Audit Check List"
              : "Create Audit Check List"}
          </h1>
          <div className="grid grid-cols-4 gap-8 text-sm">
            <div>
              <span className="text-gray-600 font-medium">Checklist Name</span>
              <div className="text-blue-600 font-medium">
                {formData?.checkList || "N/A"}
              </div>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Item No</span>
              <div className="text-blue-600 font-medium">
                {formData?.itemNo || "N/A"}
              </div>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Risk Rating</span>
              <div className="text-blue-600 font-medium">
                {formData?.riskRating || "N/A"}
              </div>
            </div>
            <div>
              <span className="text-gray-600 font-medium">Audit Type</span>
              <div className="text-blue-600 font-medium">
                {formData?.auditType || "N/A"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Container - Flexible height with fixed footer */}
      <div className="flex-1 flex flex-col mx-8 mb-8 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Form Header - Fixed height */}
        <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-200 flex justify-end">
          <button
            onClick={addSection}
            className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
          >
            <Grid3X3 size={16} />
            ADD SECTION
          </button>
        </div>

        {/* Form Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto">
          {sections.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <p className="text-lg">
                No sections added yet. Click "ADD SECTION" to get started.
              </p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {sections.map((section, sectionIndex) => (
                <div
                  key={section.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  {/* Section Header - Light Gray Strip with static delete button */}
                  <div className="bg-gray-200 px-6 py-3 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={section.title}
                        onChange={(e) =>
                          updateSectionTitle(section.id, e.target.value)
                        }
                        className="text-lg font-medium text-gray-800 bg-transparent border-none outline-none flex-1 placeholder-gray-500"
                        placeholder="Enter Header"
                      />
                    </div>
                    <button
                      onClick={() => deleteSection(section.id)}
                      className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      title="Delete Section"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Questions Container */}
                  <div className="p-6 space-y-4">
                    {section.questions.map((question, questionIndex) => (
                      <div key={question.id} className="space-y-2">
                        {/* Question Label */}
                        <div className="text-gray-700 font-medium">
                          {questionIndex + 1}) Question
                        </div>

                        {/* Question Input with Actions */}
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={question.text}
                            onChange={(e) =>
                              updateQuestion(
                                section.id,
                                question.id,
                                e.target.value,
                              )
                            }
                            placeholder="Enter Question"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />

                          {/* Show Plus button only for the last question */}
                          {questionIndex === section.questions.length - 1 ? (
                            <button
                              onClick={() =>
                                handlePlusClick(section.id, question.id)
                              }
                              className="flex items-center justify-center font-semibold gap-3 bg-gray-500 text-white px-1 py-1 rounded-full"
                              title="Add Question"
                            >
                              <Plus size={20} />
                            </button>
                          ) : (
                            /* Show Edit and Delete buttons for all other questions */
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  toggleQuestionEdit(section.id, question.id)
                                }
                                className="p-2 text-blue-500 hover:bg-blue-50 rounded-full"
                                title="Edit Question"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() =>
                                  deleteQuestion(section.id, question.id)
                                }
                                className="p-2 text-red-500 hover:bg-red-50 rounded-full"
                                title="Delete Question"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Footer - Fixed at bottom with consistent height */}
        <div className="flex-shrink-0 bg-[#61748e] px-6 py-2 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handlePrevious}
            className="flex items-center justify-center font-semibold gap-3 bg-[#869ab6] text-white px-4 py-2 rounded-full"
          >
            PREVIOUS
          </button>
          {mode === "edit" ? (
            <button
              onClick={handelUpdate}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              UPDATE
            </button>
          ) : (
            <button
              onClick={handleSave}
              className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
            >
              SAVE
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateCheckList;
